import { Tool, type ToolContext } from './base/Tool';
import type { Element, Point } from '@/types';
import { getElementAtPoint } from './select/hit-testing';
import {
  getResizeHandleAtPoint,
  calculateSingleResizeUpdates,
} from './select/resizing';
import { getControlPointAtPoint } from './select/control-points';
import { findNearestElement } from '@/utils/geometry/connections';
import { getCombinedBounds } from '@/utils/geometry/bounds';

// type PointElement = Element & { type: 'point'; x: number; y: number }; // Unused, keeping for future reference

export class SelectTool extends Tool {
  constructor() {
    super('select', 'Select');
  }

  onStart(context: ToolContext): Element | null {
    const { rootStore, currentPoint, ctrlKey } = context;
    const { projectStore, uiStore } = rootStore;

    // Priority -1: Add a new knot
    if (uiStore.knotInsertionPreview) {
      projectStore.addElementPoint(
        uiStore.knotInsertionPreview.elementId,
        uiStore.knotInsertionPreview.index,
        uiStore.knotInsertionPreview.position
      );
      const element = projectStore.getElementById(
        uiStore.knotInsertionPreview.elementId
      );
      // Immediately start moving the new point
      uiStore.setSelectToolMode({
        type: 'moving-control-point',
        elementId: uiStore.knotInsertionPreview.elementId,
        pointIndex: uiStore.knotInsertionPreview.index,
        workingPoints:
          element && (element.type === 'line' || element.type === 'arrow')
            ? element.points
            : [],
      });
      uiStore.setKnotInsertionPreview(null);
      return null;
    }

    // Priority 0: Check for control point interaction
    const controlPointInfo = getControlPointAtPoint(
      currentPoint,
      projectStore.elements,
      projectStore.selectedElementIds
    );
    if (controlPointInfo) {
      const element = projectStore.getElementById(controlPointInfo.elementId);
      if (element && (element.type === 'line' || element.type === 'arrow')) {
        uiStore.setSelectToolMode({
          type: 'moving-control-point',
          ...controlPointInfo,
          workingPoints: [...element.points],
        });
      }
      return null;
    }

    // Priority 1: Check for resize handle interaction
    const handle = getResizeHandleAtPoint(
      currentPoint,
      projectStore.elements,
      Array.from(projectStore.selectedElementIds.keys())
    );
    if (handle && projectStore.selectedElements.length === 1) {
      const element = projectStore.selectedElements[0];
      if (element) {
        uiStore.setSelectToolMode({
          type: 'resizing',
          handle: handle.handle,
          elementId: element.id,
          initialElement: JSON.parse(JSON.stringify(element)),
          startPoint: currentPoint,
        });
      }
      return null;
    }

    // Priority 2: Check for element click
    const clickedElement = getElementAtPoint(
      currentPoint,
      projectStore.elements
    );
    if (clickedElement) {
      if (ctrlKey) {
        projectStore.selectedElementIds.has(clickedElement.id)
          ? projectStore.removeFromSelection(clickedElement.id)
          : projectStore.addToSelection(clickedElement.id);
      } else if (!projectStore.selectedElementIds.has(clickedElement.id)) {
        projectStore.select(clickedElement.id);
      }

      // Prepare for dragging selected elements
      const initialPositions = new Map<string, Point>();
      const initialPoints = new Map<string, Point[]>();
      projectStore.selectedElements.forEach(el => {
        initialPositions.set(el.id, { x: el.x, y: el.y });
        // Store initial points for lines and arrows
        if ((el.type === 'line' || el.type === 'arrow') && (el as any).points) {
          initialPoints.set(el.id, [...(el as any).points]);
        }
      });
      uiStore.setSelectToolMode({
        type: 'dragging',
        elementIds: Array.from(projectStore.selectedElementIds.keys()),
        startPoint: currentPoint,
        initialPositions,
        initialPoints,
      } as any);
    } else {
      // Priority 3: Start a selection rectangle
      // Start a selection rectangle
      projectStore.clearSelection();
      uiStore.setSelectToolMode({ type: 'selection-rectangle' });
    }
    return null;
  }

  onMove(context: ToolContext): Element | undefined {
    const { rootStore, currentPoint } = context;
    const { projectStore, uiStore } = rootStore;
    const { selectToolMode, startPoint, isDrawing } = uiStore;

    if (!isDrawing || !startPoint) {
      return undefined;
    }

    switch (selectToolMode.type) {
      case 'dragging': {
        const dx = currentPoint.x - selectToolMode.startPoint.x;
        const dy = currentPoint.y - selectToolMode.startPoint.y;
        const updates = selectToolMode.elementIds
          .map(elementId => {
            const element = projectStore.getElementById(elementId);
            const initialPos = selectToolMode.initialPositions?.get(elementId);
            if (!initialPos || !element) return null;

            // Handle lines and arrows specially - move their points
            if (element.type === 'line' || element.type === 'arrow') {
              const initialPoints = (selectToolMode as any).initialPoints?.get(
                elementId
              );
              if (initialPoints && initialPoints.length > 0) {
                const movedPoints = initialPoints.map((point: Point) => ({
                  x: point.x + dx,
                  y: point.y + dy,
                }));
                return {
                  id: elementId,
                  updates: { points: movedPoints },
                };
              }
            }

            // Handle regular elements - move their x,y position
            return {
              id: elementId,
              updates: { x: initialPos.x + dx, y: initialPos.y + dy },
            };
          })
          .filter(Boolean);
        projectStore.updateElements(updates as any);
        break;
      }
      case 'resizing': {
        const dx = currentPoint.x - selectToolMode.startPoint.x;
        const dy = currentPoint.y - selectToolMode.startPoint.y;
        const updates = calculateSingleResizeUpdates(
          selectToolMode.initialElement,
          selectToolMode.handle,
          dx,
          dy
        );
        if (Object.keys(updates).length > 0) {
          // Use updateElements instead of updateElement to trigger group child scaling
          projectStore.updateElements([
            {
              id: selectToolMode.elementId,
              updates,
            },
          ]);
        }
        break;
      }
      case 'moving-control-point': {
        const { elementId, pointIndex } = selectToolMode;
        const element = projectStore.getElementById(elementId) as any;

        let finalPoint = currentPoint;
        let connectionUpdate: any = {};

        if (
          element &&
          (element.type === 'line' || element.type === 'arrow') &&
          (pointIndex === 0 || pointIndex === element.points.length - 1)
        ) {
          const connection = findNearestElement(
            currentPoint,
            projectStore.elements,
            [elementId]
          );

          if (connection) {
            finalPoint = connection.connectionPoint;
            const newConnection = {
              elementId: connection.element.id,
              pointId: connection.connectionPoint.id,
            };
            if (pointIndex === 0) {
              connectionUpdate = { startConnection: newConnection };
            } else {
              connectionUpdate = { endConnection: newConnection };
            }
          } else {
            // Detach if previously connected
            if (pointIndex === 0 && element.startConnection) {
              connectionUpdate = { startConnection: null };
            } else if (
              pointIndex === element.points.length - 1 &&
              element.endConnection
            ) {
              connectionUpdate = { endConnection: null };
            }
          }
        }

        projectStore.updateElementPoint(elementId, pointIndex, finalPoint);
        if (Object.keys(connectionUpdate).length > 0) {
          projectStore.updateElement({
            id: elementId,
            updates: connectionUpdate,
          });
        }
        break;
      }
      case 'selection-rectangle': {
        const bounds = this.getBounds({ startPoint, currentPoint });

        if (bounds.width < 2 && bounds.height < 2) {
          return undefined;
        }

        return {
          id: 'selection-rectangle-preview',
          type: 'rectangle',
          x: bounds.x,
          y: bounds.y,
          width: bounds.width,
          height: bounds.height,
          style: {
            strokeColor: '#007acc',
            strokeWidth: 1,
            strokeStyle: 'dashed',
            fillColor: 'rgba(0, 122, 204, 0.1)',
            fillPattern: 'solid',
            opacity: 1,
          },
          isSelected: false,
          zIndex: 1000,
          createdAt: 0,
          updatedAt: 0,
        } as Element;
      }
    }
    return undefined;
  }

  onEnd(context: ToolContext): void {
    const {
      rootStore,
      currentPoint,
      ctrlKey,
      startPoint: contextStartPoint,
    } = context;
    const { uiStore, projectStore } = rootStore;
    const { startPoint: uiStartPoint, isDrawing } = uiStore;

    if (uiStore.selectToolMode.type === 'selection-rectangle') {
      // Use context startPoint as fallback
      const startPoint = uiStartPoint || contextStartPoint;

      if (!isDrawing || !startPoint) {
        return;
      }

      const bounds = this.getBounds({ startPoint, currentPoint });

      if (bounds.width > 2 || bounds.height > 2) {
        const selectedIds = projectStore.elements
          .filter(el => {
            if (!el) return false;
            // Skip elements that belong to a group - only select groups themselves
            if (el.groupId) return false;
            const elBounds = getCombinedBounds([el]);
            if (!elBounds) return false;
            return !(
              elBounds.x > bounds.x + bounds.width ||
              elBounds.x + elBounds.width < bounds.x ||
              elBounds.y > bounds.y + bounds.height ||
              elBounds.y + elBounds.height < bounds.y
            );
          })
          .map(el => el.id);

        if (ctrlKey) {
          selectedIds.forEach(id => projectStore.addToSelection(id));
        } else {
          projectStore.selectElements(selectedIds);
        }
      }
    }

    if (uiStore.selectToolMode.type !== 'idle') {
      projectStore.takeSnapshot();
    }

    uiStore.setSelectToolMode({ type: 'idle' });
  }

  onCancel(context: ToolContext): void {
    const { uiStore } = context.rootStore;
    uiStore.setSelectToolMode({ type: 'idle' });
  }

  getCursor(context: ToolContext): string {
    const { currentPoint, rootStore } = context;
    const { projectStore } = rootStore;

    // Check for control point hover
    const controlPointInfo = getControlPointAtPoint(
      currentPoint,
      projectStore.elements,
      projectStore.selectedElementIds
    );
    if (controlPointInfo) {
      return 'move';
    }

    const resizeHandle = getResizeHandleAtPoint(
      currentPoint,
      projectStore.elements,
      Array.from(projectStore.selectedElementIds.keys())
    );
    if (resizeHandle) {
      return (resizeHandle as any).cursor || 'default';
    }
    return 'default';
  }

  onDoubleClick({ rootStore, currentPoint }: ToolContext) {
    const { projectStore, uiStore } = rootStore;
    const hitElement = getElementAtPoint(currentPoint, projectStore.elements);

    if (
      hitElement &&
      (hitElement.type === 'rectangle' ||
        hitElement.type === 'ellipse' ||
        hitElement.type === 'diamond' ||
        hitElement.type === 'text')
    ) {
      uiStore.startEditingText(hitElement.id);
    } else {
      // Create new text element on empty canvas double-click
      // Take snapshot first for undo/redo
      projectStore.takeSnapshot();

      projectStore.addTextElementAndStartEditing({
        type: 'text',
        x: currentPoint.x,
        y: currentPoint.y,
        text: 'Text',
        width: 200,
        height: 60,
        style: {
          ...uiStore.persistentStyle,
          // Text specific overrides
          textAlign: 'left',
        },
      } as any);
    }
    // SVG elements don't support text editing, so they are excluded
  }
}
